/**
 * Small shared colour helpers for the base-view renderer: tinting variants
 * off a base hex colour, and a deterministic hash-to-palette picker so NPC
 * placeholders stay within the chestnut/rust/bronze/verdigris art direction
 * instead of full-spectrum random hues.
 */
import * as THREE from 'three';

const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));

/**
 * Tint a base colour. `factor` < 1 darkens (multiply toward black),
 * `factor` > 1 lightens (lerp toward white), `factor` === 1 is a no-op.
 */
export function tint(hex: number, factor: number): number {
  const c = new THREE.Color(hex);
  if (factor > 1) c.lerp(new THREE.Color(0xffffff), clamp01(factor - 1));
  else if (factor < 1) c.multiplyScalar(Math.max(0, factor));
  return c.getHex();
}

/** Curated chestnut / rust / bronze / verdigris tones for NPC placeholders. */
export const NPC_PALETTE: readonly number[] = [
  0x9a4a30, // rust
  0xb8763a, // bronze
  0x6d8f6a, // verdigris green
  0x4f7a78, // verdigris teal
  0x8a5a3a, // chestnut
  0xc99a4e, // ochre / bronze
  0x6b5236, // leather brown
  0x7a6a3f, // faded olive
];

/** Deterministic string hash (FNV-1a) → uint32. */
function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Pick a palette colour for an NPC id, with a small deterministic tint spread. */
export function colorForNpc(npcId: string): number {
  const h = hashString(npcId);
  const base = NPC_PALETTE[h % NPC_PALETTE.length];
  const spread = ((h >>> 8) % 21) / 100 - 0.1; // -0.10 .. +0.10
  return tint(base, 1 + spread);
}
